"use client";

import { cn } from "@repo/ui";
import Image from "next/image";
import { useEffect, useState } from "react";

import { BgPatternOverlay } from "@/app/(marketing)/_components/bg-pattern-overlay";
import { Container } from "@/app/(marketing)/_components/ui/container";

import { AppleLogo, WindowsLogo } from "./os-icons";
import { useDetectedOS } from "./use-detected-os";

type SetupOS = "mac" | "windows";

interface Step {
  title: string;
  description: string;
}

const MACOS_STEPS: Step[] = [
  {
    title: "Open the .dmg",
    description:
      "Double-click the downloaded Terac .dmg, then drag the Terac icon into your Applications folder.",
  },
  {
    title: "Launch Terac",
    description: "Open Terac from your Applications folder.",
  },
  {
    title: "Log in",
    description:
      "Sign in with the same account you use on terac.com so recordings link to your profile and payouts.",
  },
  {
    title: "Grant permissions",
    description:
      "Terac guides you through Screen Recording, Accessibility and Microphone in System Settings. Quit and reopen after enabling Screen Recording.",
  },
  {
    title: "Record",
    description:
      "Pick your assigned task, record yourself doing it, then press Stop. The session uploads automatically.",
  },
];

const WINDOWS_STEPS: Step[] = [
  {
    title: "Run the installer",
    description:
      "Double-click the downloaded Terac setup .exe and follow the prompts.",
  },
  {
    title: "Clear SmartScreen",
    description:
      "On a brand-new release Windows may show a blue dialog. Click More info, then Run anyway.",
  },
  {
    title: "Log in",
    description:
      "Sign in with the same account you use on terac.com so recordings link to your profile and payouts.",
  },
  {
    title: "Allow input monitoring",
    description:
      "Terac records keyboard and mouse input during a session. If Defender or your antivirus asks, allow Terac to run.",
  },
  {
    title: "Record",
    description:
      "Pick your assigned task, record yourself doing it, then press Stop. The session uploads automatically.",
  },
];

const TABS: {
  key: SetupOS;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { key: "mac", label: "macOS", icon: AppleLogo },
  { key: "windows", label: "Windows", icon: WindowsLogo },
];

export default function SetupSteps() {
  const detected = useDetectedOS();
  const [active, setActive] = useState<SetupOS>("mac");

  // Default the visible platform to the visitor's OS once detected.
  useEffect(() => {
    if (detected === "windows") {
      setActive("windows");
    } else if (detected === "mac") {
      setActive("mac");
    }
  }, [detected]);

  const steps = active === "windows" ? WINDOWS_STEPS : MACOS_STEPS;

  return (
    <section id="setup" className="scroll-mt-4 py-4 lg:py-6">
      <div className="bg-marketing-blue relative mx-4 overflow-hidden rounded-[1.25rem]">
        <div
          className="pointer-events-none absolute inset-0 z-0 mix-blend-color-dodge select-none"
          aria-hidden="true"
        >
          <Image
            src="/marketing/backgrounds/white-texture.jpg"
            alt=""
            fill
            className="object-cover"
            draggable={false}
          />
        </div>

        <BgPatternOverlay
          tileCount={1}
          className="absolute inset-0 z-0 overflow-hidden opacity-40"
        />

        <div className="relative z-10 py-12 lg:py-16">
          <Container>
            <div className="mb-8 flex flex-col items-start gap-4">
              <div className="rounded-full border border-white/15 bg-white/10 px-3 py-1 backdrop-blur-sm">
                <p className="font-mono text-sm tracking-[-0.28px] text-white/90">
                  Setup
                </p>
              </div>
              <h2 className="text-heading-32 md:text-heading-40 lg:text-heading-48 max-w-[600px] text-balance text-white">
                A few minutes to your first recording
              </h2>
              <p className="max-w-[500px] text-lg font-medium text-white/80">
                From download to recording, here is the whole path.
              </p>
            </div>

            <div
              className="mb-8 inline-flex gap-1 rounded-full border border-white/15 bg-white/10 p-1 backdrop-blur-sm"
              role="tablist"
              aria-label="Choose your operating system"
            >
              {TABS.map((tab) => {
                const selected = active === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => {
                      setActive(tab.key);
                    }}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors",
                      selected
                        ? "text-marketing-blue bg-white"
                        : "text-white/70 hover:text-white",
                    )}
                  >
                    <tab.icon className="size-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <ol className="max-w-[640px] border-t border-white/15">
              {steps.map((step, index) => (
                <li
                  key={step.title}
                  className="flex gap-5 border-b border-white/15 py-4"
                >
                  <span className="font-mono text-base font-medium text-white/50">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="mb-1 font-medium text-white">
                      {step.title}
                    </h3>
                    <p className="text-sm text-white/70">{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Container>
        </div>
      </div>
    </section>
  );
}
